import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Award, User, Save, Clock, Calendar, ChevronLeft, ChevronRight, CheckCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useSupabaseUser } from '@/hooks/use-supabase-user';
import { Label } from '@/components/ui/label';
import { useShiftaskCriteria, ShiftaskEvaluationCriterion } from '@/hooks/useShiftaskEvaluation';
import { useLanguage } from '@/contexts/LanguageContext';

interface ShiftaskEvaluationFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  guardId?: string;
  guardName?: string;
  guardAvatar?: string;
}

interface EvaluationResponse {
  criterion_id: string;
  score: number;
  comments?: string;
}

interface EvaluationState {
  responses: EvaluationResponse[];
  currentStep: number;
  notes: string;
}

export const ShiftaskEvaluationFormModal: React.FC<ShiftaskEvaluationFormModalProps> = ({
  open,
  onOpenChange,
  guardId,
  guardName,
  guardAvatar,
}) => {
  const { user } = useSupabaseUser();
  const queryClient = useQueryClient();
  const { data: criteria = [], isLoading: criteriaLoading } = useShiftaskCriteria();
  const { t } = useLanguage();
  
  const [evaluationState, setEvaluationState] = React.useState<EvaluationState>({
    responses: [],
    currentStep: 0,
    notes: '',
  });

  const [isCompleted, setIsCompleted] = React.useState(false);

  // Reset state when modal opens
  React.useEffect(() => {
    if (open && criteria.length > 0) {
      setEvaluationState({
        responses: criteria.map(criterion => ({
          criterion_id: criterion.id,
          score: 4, // Default to middle score
          comments: '',
        })),
        currentStep: 0,
        notes: '',
      });
      setIsCompleted(false);
    }
  }, [open, criteria]);

  // Calculate overall score based on weighted responses
  const calculateOverallScore = React.useCallback(() => {
    if (evaluationState.responses.length === 0 || criteria.length === 0) return 0;
    
    let totalWeightedScore = 0;
    let totalWeight = 0;
    
    evaluationState.responses.forEach(response => {
      const criterion = criteria.find(c => c.id === response.criterion_id);
      if (criterion) {
        // Convert 1-7 scale to percentage (1=14.3%, 7=100%)
        const scorePercentage = ((response.score - 1) / 6) * 100;
        totalWeightedScore += scorePercentage * criterion.weight;
        totalWeight += criterion.weight;
      }
    });
    
    return totalWeight > 0 ? Math.round(totalWeightedScore / totalWeight) : 0;
  }, [evaluationState.responses, criteria]);

  const saveEvaluation = useMutation({
    mutationFn: async () => {
      if (!user || !guardId) {
        throw new Error('Missing required data');
      }

      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentSemester = currentDate.getMonth() >= 6 ? 'H2' : 'H1';
      const overallScore = calculateOverallScore();

      // First, create the evaluation
      const evaluationData = {
        overall_score: overallScore,
        notes: evaluationState.notes,
        guard_id: guardId,
        evaluator_id: user.id,
        evaluation_date: currentDate.toISOString().split('T')[0],
        evaluation_period: currentSemester,
        evaluation_year: currentYear,
        status: 'active' as const,
        valid_from: currentDate.toISOString().split('T')[0],
        valid_until: currentSemester === 'H1' 
          ? `${currentYear}-06-30`
          : `${currentYear}-12-31`,
      };

      const { data: evaluation, error: evalError } = await supabase
        .from('ovit_evaluations')
        .insert([evaluationData])
        .select()
        .single();

      if (evalError) throw evalError;

      // Then, save all responses
      const responsesData = evaluationState.responses.map(response => ({
        evaluation_id: evaluation.id,
        criterion_id: response.criterion_id,
        score: response.score,
        comments: response.comments || null,
      }));

      const { error: responsesError } = await supabase
        .from('ovit_evaluation_responses')
        .insert(responsesData);

      if (responsesError) throw responsesError;

      return evaluation;
    },
    onSuccess: () => {
      toast.success(t('shiftaskEvaluationSavedSuccess') || 'Η αξιολόγηση Shiftask αποθηκεύτηκε επιτυχώς!');
      queryClient.invalidateQueries({ queryKey: ['shiftask-evaluation', guardId] });
      queryClient.invalidateQueries({ queryKey: ['current-shiftask-evaluation'] });
      queryClient.invalidateQueries({ queryKey: ['shiftask-evaluations-all'] });
      queryClient.invalidateQueries({ queryKey: ['guard-performance'] });
      onOpenChange(false);
    },
    onError: (error: any) => {
      console.error('Error saving evaluation:', error);
      const errorMessage = error?.message || error?.toString() || 'Unknown error';
      toast.error(`${t('errorSavingEvaluation') || 'Error saving evaluation'}: ${errorMessage}`);
    },
  });

  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();  
  const currentSemester = currentDate.getMonth() >= 6 ? 'H2' : 'H1';
  const semesterLabel = currentSemester === 'H1' ? (t('firstSemester') || 'Α΄ Εξάμηνο') : (t('secondSemester') || 'Β΄ Εξάμηνο');
  
  const currentCriterion = criteria[evaluationState.currentStep];
  const overallScore = calculateOverallScore();
  const completedSteps = evaluationState.currentStep;
  const totalSteps = criteria.length;

  const updateResponse = (criterionId: string, score: number, comments?: string) => {
    setEvaluationState(prev => ({
      ...prev,
      responses: prev.responses.map(response =>
        response.criterion_id === criterionId
          ? { ...response, score, comments: comments || response.comments }
          : response
      ),
    }));
  };

  const nextStep = () => {
    if (evaluationState.currentStep < criteria.length - 1) {
      setEvaluationState(prev => ({ ...prev, currentStep: prev.currentStep + 1 }));
    } else {
      setIsCompleted(true);
    }
  };

  const prevStep = () => {
    if (evaluationState.currentStep > 0) {
      setEvaluationState(prev => ({ ...prev, currentStep: prev.currentStep - 1 }));
    }
  };

  const getScoreLabel = (score: number) => {
    const labels = [
      'Πολύ Χαμηλό', 'Χαμηλό', 'Κάτω του Μέσου', 'Μέσο', 'Πάνω από το Μέσο', 'Υψηλό', 'Εξαιρετικό'
    ];
    return labels[score - 1] || 'Μέσο';
  };

  const getScoreColor = (score: number) => {
    if (score >= 6) return 'text-green-600';
    if (score >= 5) return 'text-blue-600'; 
    if (score >= 4) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getCategoryLabel = (category: string) => {
    const categoryLabels: Record<string, string> = {
      'personal_qualities': t('personalQualities') || 'Προσωπικές Ικανότητες',
      'professional_skills': t('professionalSkills') || 'Επαγγελματικές Δεξιότητες',
      'team_collaboration': t('teamCollaboration') || 'Ομαδική Συνεργασία'
    };
    return categoryLabels[category] || category;
  };

  // Translation mapping for criteria
  const getCriterionTranslation = (greekName: string, greekDescription: string) => {
    const criteriaTranslations: Record<string, {name: string, description: string}> = {
      'Αξιοπιστία': {name: t('reliability') || 'Reliability', description: t('reliabilityEvalDesc') || 'Reliability and trustworthiness'},
      'Ηθική και Ακεραιότητα': {name: t('ethicsAndIntegrity') || 'Ethics and Integrity', description: t('ethicsDesc') || 'Ethical behavior and character integrity'},
      'Προσαρμοστικότητα': {name: t('adaptability') || 'Adaptability', description: t('adaptabilityDesc') || 'Ability to adapt to new situations'},
      'Υπευθυνότητα': {name: t('responsibility') || 'Responsibility', description: t('responsibilityDesc') || 'Sense of responsibility and accountability'},
      'Φιλότιμο': {name: t('dedication') || 'Dedication', description: t('dedicationDesc') || 'Dedication and devotion to duty'},
      'Αντίδραση σε Κρίσεις': {name: t('crisisResponse') || 'Crisis Response', description: t('crisisResponseDesc') || 'Ability to manage crises and emergencies'},
      'Διακριτικότητα': {name: t('discretion') || 'Discretion', description: t('discretionDesc') || 'Discretion and confidentiality'},
      'Εγρήγορση': {name: t('alertness') || 'Alertness', description: t('alertnessDesc') || 'Attention and vigilance during service'},
      'Εμφάνιση': {name: t('appearance') || 'Appearance', description: t('appearanceDesc') || 'Professional appearance and presentation'},
      'Επαγγελματισμός': {name: t('professionalism') || 'Professionalism', description: t('professionalismEvalDesc') || 'Professional conduct and attitude'},
      'Τεχνικές Δεξιότητες': {name: t('technicalSkills') || 'Technical Skills', description: t('technicalSkillsDesc') || 'Technical knowledge and skills'},
      'Τεχνικές Γνώσεις': {name: t('technicalKnowledge') || 'Technical Knowledge', description: t('technicalKnowledgeDesc') || 'Knowledge of equipment and security procedures'},
      'Πρωτοβουλία': {name: t('initiative') || 'Initiative', description: t('initiativeDesc') || 'Ability to take initiative and act proactively'},
      'Οργάνωση': {name: t('organization') || 'Organization', description: t('organizationDesc') || 'Work organization and time management ability'},
      'Φυσική Κατάσταση': {name: t('physicalCondition') || 'Physical Condition', description: t('physicalConditionDesc') || 'Physical preparation and endurance'},
      'Επικοινωνία': {name: t('communication') || 'Communication', description: t('communicationEvalDesc') || 'Effective and clear communication'},
      'Ηγετικές Ικανότητες': {name: t('leadershipSkills') || 'Leadership Skills', description: t('leadershipSkillsDesc') || 'Ability to lead and take initiative'},
      'Ομαδικότητα': {name: t('teamwork') || 'Teamwork', description: t('teamworkDesc') || 'Ability to cooperate and work in a team'},
      'Σεβασμός': {name: t('respect') || 'Respect', description: t('respectDesc') || 'Respect for colleagues and superiors'},
      'Συνέπεια': {name: t('consistency') || 'Consistency', description: t('consistencyDesc') || 'Consistency and punctuality'},
      'Συνεργασία': {name: t('cooperation') || 'Cooperation', description: t('cooperationDesc') || 'Cooperation with colleagues and supervisors'}
    };
    
    const translation = criteriaTranslations[greekName];
    if (translation) {
      return translation;
    }
    return {name: greekName, description: greekDescription};
  };

  if (criteriaLoading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl">
          <div className="flex items-center justify-center p-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p>{t('loadingEvaluationCriteria') || 'Φόρτωση κριτηρίων αξιολόγησης...'}</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Award className="h-5 w-5 text-blue-600" />
            {isCompleted ? (t('completeShiftaskEvaluation') || 'Ολοκλήρωση Αξιολόγησης Shiftask') : (t('newShiftaskEvaluation') || 'Νέα Αξιολόγηση Shiftask')}
          </DialogTitle>
          <DialogDescription className="space-y-4">
            {/* Guard Avatar and Info */}
            <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg border">
              <Avatar className="h-16 w-16 ring-2 ring-blue-500/20">
                <AvatarImage src={guardAvatar} alt={guardName} />
                <AvatarFallback className="text-lg font-semibold bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                  {guardName?.split(' ').map(n => n[0]).join('').toUpperCase() || 'FG'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <h3 className="font-semibold text-xl text-foreground mb-1">{guardName}</h3>
                <p className="text-sm text-blue-600 font-medium mb-2">{t('internalEvaluation') || 'Εσωτερική Αξιολόγηση'}</p>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="bg-blue-100 text-blue-700 border-blue-200">
                    <Calendar className="w-3 h-3 mr-1" />
                    {semesterLabel} {currentYear}
                  </Badge>
                  <Badge variant="outline" className="bg-green-100 text-green-700 border-green-200">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-1"></div>
                    {t('active') || 'Ενεργό'}
                  </Badge>
                </div>
              </div>
            </div>

            {/* Progress Bar */}
            {!isCompleted && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>{t('evaluationProgram') || 'Πρόγραμμα αξιολόγησης'}</span>
                  <span>{completedSteps} {t('of') || 'από'} {totalSteps}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(completedSteps / totalSteps) * 100}%` }}
                  />
                </div>
              </div>
            )}
          </DialogDescription>
        </DialogHeader>

        {!isCompleted ? (
          <div className="space-y-6">
            {/* Current Question */}
            {currentCriterion && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between mb-2">
                    <Badge variant="outline" className="text-xs">
                      {getCategoryLabel(currentCriterion.category)}
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      {evaluationState.currentStep + 1} / {totalSteps}
                    </span>
                  </div>
                  <CardTitle className="text-xl mb-2">{getCriterionTranslation(currentCriterion.name, currentCriterion.description || '').name}</CardTitle>
                  <CardDescription>{getCriterionTranslation(currentCriterion.name, currentCriterion.description || '').description}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Rating Scale */}
                  <div className="space-y-4">
                    <Label className="text-base font-medium">
                      {t('rateOnScale') || 'Αξιολογήστε σε κλίμακα 1-7'} (1 = {t('veryLow') || 'Πολύ Χαμηλό'}, 7 = {t('excellent') || 'Εξαιρετικό'})
                    </Label>
                    <RadioGroup
                      value={evaluationState.responses.find(r => r.criterion_id === currentCriterion.id)?.score?.toString() || '4'}
                      onValueChange={(value) => updateResponse(currentCriterion.id, parseInt(value))}
                      className="grid grid-cols-7 gap-4"
                    >
                      {[1, 2, 3, 4, 5, 6, 7].map((score) => (
                        <div key={score} className="flex flex-col items-center space-y-2">
                          <RadioGroupItem value={score.toString()} id={`score-${score}`} />
                          <Label 
                            htmlFor={`score-${score}`} 
                            className={`text-xs text-center cursor-pointer ${getScoreColor(score)}`}
                          >
                            {score}
                          </Label>
                        </div>
                      ))}
                    </RadioGroup>
                    
                    {/* Score Description */}
                    <div className="text-center p-3 bg-muted/50 rounded-lg">
                      <span className={`text-sm font-medium ${getScoreColor(
                        evaluationState.responses.find(r => r.criterion_id === currentCriterion.id)?.score || 4
                      )}`}>
                        {getScoreLabel(evaluationState.responses.find(r => r.criterion_id === currentCriterion.id)?.score || 4)}
                      </span>
                    </div>
                  </div>

                  {/* Optional Comments */}
                  <div className="space-y-2">
                    <Label htmlFor="comments" className="text-sm">
                      {t('commentsOptional') || 'Σχόλια (προαιρετικό)'}
                    </Label>
                    <Textarea
                      id="comments"
                      placeholder={t('addExamplesPlaceholder') || 'Προσθέστε συγκεκριμένα παραδείγματα ή επεξηγήσεις...'}
                      value={evaluationState.responses.find(r => r.criterion_id === currentCriterion.id)?.comments || ''}
                      onChange={(e) => updateResponse(currentCriterion.id, 
                        evaluationState.responses.find(r => r.criterion_id === currentCriterion.id)?.score || 4, 
                        e.target.value
                      )}
                      rows={3}
                    />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between">
              <Button 
                type="button" 
                variant="outline" 
                onClick={prevStep}
                disabled={evaluationState.currentStep === 0}
                className="gap-2"
              >
                <ChevronLeft className="h-4 w-4" />
                {t('previous') || 'Προηγούμενο'}
              </Button>
              
              <Button 
                type="button" 
                onClick={nextStep}
                className="gap-2"
              >
                {evaluationState.currentStep === criteria.length - 1 ? (
                  <>
                    <CheckCircle className="h-4 w-4" />
                    {t('complete') || 'Ολοκλήρωση'}
                  </>
                ) : (
                  <>
                    {t('next') || 'Επόμενο'}
                    <ChevronRight className="h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          </div>
        ) : (
          /* Completion Summary */
          <div className="space-y-6">
            {/* Overall Score Display */}
            <Card className="border-green-200 bg-green-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-green-800">
                  <CheckCircle className="h-5 w-5" />
                  {t('overallEvaluationScore') || 'Συνολικό Σκορ Αξιολόγησης'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center">
                  <div className={`text-4xl font-bold mb-2 ${getScoreColor(Math.round(overallScore / 100 * 7))}`}>
                    {overallScore}%
                  </div>
                  <div className="text-lg text-muted-foreground mb-4">
                    {t('basedOnCriteria')?.replace('{count}', criteria.length.toString()) || `Βασισμένο σε ${criteria.length} κριτήρια αξιολόγησης`}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Category Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle>{t('analysisByCategory') || 'Ανάλυση ανά Κατηγορία'}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {Object.entries(
                    criteria.reduce((acc, criterion) => {
                      if (!acc[criterion.category]) acc[criterion.category] = [];
                      acc[criterion.category].push(criterion);
                      return acc;
                    }, {} as Record<string, ShiftaskEvaluationCriterion[]>)
                  ).map(([category, categoryCriteria]) => {
                    const categoryResponses = categoryCriteria.map(c => 
                      evaluationState.responses.find(r => r.criterion_id === c.id)
                    ).filter(Boolean);
                    const avgScore = categoryResponses.length > 0 
                      ? categoryResponses.reduce((sum, r) => sum + (r?.score || 0), 0) / categoryResponses.length 
                      : 0;
                    
                    return (
                      <div key={category} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                        <span className="font-medium">{getCategoryLabel(category)}</span>
                        <div className="text-right">
                          <div className={`text-lg font-bold ${getScoreColor(avgScore)}`}>
                            {avgScore.toFixed(1)}/7
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {categoryCriteria.length} {t('criteria') || 'κριτήρια'}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Notes Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  {t('generalNotes') || 'Γενικές Σημειώσεις'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  placeholder={t('addGeneralNotesPlaceholder') || 'Προσθέστε γενικές σημειώσεις για την αξιολόγηση...'}
                  value={evaluationState.notes}
                  onChange={(e) => setEvaluationState(prev => ({ ...prev, notes: e.target.value }))}
                  rows={4}
                />
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="flex justify-between pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setIsCompleted(false)}
                className="gap-2"
              >
                <ChevronLeft className="h-4 w-4" />
                {t('backToQuestions') || 'Επιστροφή στις Ερωτήσεις'}
              </Button>
              
              <div className="flex gap-3">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => onOpenChange(false)}
                >
                  {t('cancel') || 'Ακύρωση'}
                </Button>
                <Button 
                  type="button"
                  onClick={() => saveEvaluation.mutate()}
                  disabled={saveEvaluation.isPending}
                  className="gap-2"
                >
                  <Save className="h-4 w-4" />
                  {t('saveEvaluation') || 'Αποθήκευση Αξιολόγησης'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};